import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { 
  getStoredAccessToken, 
  isTokenExpired, 
  redirectToSpotifyAuth 
} from './spotifyAuth';
import SongSearch from './SongSearch';
import SpotifyPlayer from './SpotifyPlayer';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './PlaylistView.css';

// Sortable Song Item Component
function SortableSongItem({ song, index, playingUri, onPlay, onEdit, onDelete, canEdit, playerReady }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: song.id || `song-${index}`,
    disabled: !canEdit  // Disable dragging for view-only users
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes}:${seconds.padStart(2, '0')}`;
  };

  return (
    <div ref={setNodeRef} style={style} className="song-item">
      <div className="song-item-main">
        {canEdit && (
          <div className="drag-handle" {...attributes} {...listeners}>
            ⋮⋮
          </div>
        )}
        
        <div className="song-order">#{index + 1}</div>
        
        {song.albumArt && (
          <div className="song-album-art">
            <img src={song.albumArt} alt={song.album || 'Album art'} />
          </div>
        )}

        <div className="song-details">
  <div className="song-name">
    {song.songName || song.name}
    {song.explicit && (
      <span style={{
        marginLeft: '8px',
        backgroundColor: '#666',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '11px',
        fontWeight: 'bold'
      }}>E</span>
    )}
  </div>
  <div className="song-artist">{song.artist}</div>
</div>

        <div className="player-info">
  <div className="player-name">
  {(song.playerNumber !== undefined && 
    song.playerNumber !== null && 
    song.playerNumber !== '' && 
    !isNaN(song.playerNumber)) 
    ? `#${song.playerNumber} ` 
    : ''}
  {song.playerName}
</div>
          {song.duration && (
            <div className="player-meta">
              {formatDuration(song.duration)}
              {song.clipDuration && 
                ` • Clip: ${song.clipDuration}s`
              }
              {song.startTime !== undefined && song.clipDuration && 
                ` (${song.startTime}s-${song.startTime + song.clipDuration}s)`
              }
            </div>
          )}
        </div>

        <div className="song-actions">
          {song.uri && (
            <button 
              className="play-btn-small"
              onClick={(e) => {
                e.stopPropagation();
                onPlay(song);
              }}
              disabled={!playerReady}
              title={
                !playerReady 
                  ? "Spotify player is loading..." 
                  : playingUri === song.uri 
                    ? "Stop song" 
                    : "Play song"
              }
              style={{ opacity: !playerReady ? 0.5 : 1 }}
            >
              {!playerReady ? '⏳' : playingUri === song.uri ? '⏹️' : '▶️'}
            </button>
          )}
          {canEdit && (
            <>
              <button 
                className="edit-btn" 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(index);
                }}
                title="Edit song"
              >
                ✏️
              </button>
              <button 
                className="delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(index);
                }}
                title="Delete song"
              >
                🗑️
              </button>
            </>
          )}
        </div>
      </div>

      {song.aiSelected && song.aiReason && (
        <div className="ai-reasoning-row">
          <div className="ai-reasoning">
            💡 <strong>AI picked this moment:</strong> {song.aiReason}
          </div>
        </div>
      )}
    </div>
  );
}

function PlaylistView({ playlist: initialPlaylist, onClose, user }) {
  const [playlist, setPlaylist] = useState(initialPlaylist);
  const [loading, setLoading] = useState(false);
  const [showSongSearch, setShowSongSearch] = useState(false);
  const [editingSongIndex, setEditingSongIndex] = useState(null);
  const [playingUri, setPlayingUri] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [showChangeSong, setShowChangeSong] = useState(false);
  const [editForm, setEditForm] = useState({
    playerName: '',
    playerNumber: '',
    startTime: 0,
    duration: 20,
    useAI: false,
    aiSuggestion: null,
    aiReason: null,
    analyzingAI: false
  });

  // Token expiry state
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);

  // Check if user can edit this playlist
  const canEdit = () => {
  // Owner can always edit
  if (playlist.ownerId === user.uid) {
    return true;
  }
  
  // Check if user has edit permission
  // Handle both array and object formats
  const details = playlist.sharedWithDetails;
  if (!details) return false;
  
  let userShare;
  if (Array.isArray(details)) {
    // Array format (old)
    userShare = details.find(share => share.userId === user.uid);
  } else {
    // Object format (new)
    userShare = details[user.uid];
  }
  
  return userShare?.permission === 'edit';
};

  const userCanEdit = canEdit();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check token expiry periodically
  useEffect(() => {
    const checkTokenExpiry = () => {
      if (isTokenExpired()) {
        setShowExpiryWarning(true);
        setPlayerReady(false);
      } else {
        setShowExpiryWarning(false);
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiry, 30000);

    return () => clearInterval(interval);
  }, []);

  // Set up real-time listener for playlist changes
  useEffect(() => {
    if (!initialPlaylist?.id) return;
    
    setLoading(true);
    
    // Real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'playlists', initialPlaylist.id),
      (playlistDoc) => {
        if (playlistDoc.exists()) {
          setPlaylist({
            id: playlistDoc.id,
            ...playlistDoc.data()
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to playlist:', error);
        setLoading(false);
      }
    );
    
    // Cleanup listener on unmount
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlaylist?.id]);

  // Check if Spotify player is ready
  useEffect(() => {
    const checkPlayerReady = setInterval(() => {
      if (window.spotifyDeviceId && !isTokenExpired()) {
        setPlayerReady(true);
        clearInterval(checkPlayerReady);
      }
    }, 500);

    // Timeout after 10 seconds if player doesn't initialize
    const timeout = setTimeout(() => {
      clearInterval(checkPlayerReady);
      if (!window.spotifyDeviceId) {
        console.warn('Spotify player did not initialize within 10 seconds');
      }
    }, 10000);

    return () => {
      clearInterval(checkPlayerReady);
      clearTimeout(timeout);
    };
  }, []);

  const handleSongAdded = () => {
    setShowSongSearch(false);
    // Real-time listener will auto-update
  };

  const handleReplaceSong = (newSongData) => {
    // Replace the song in the playlist but keep player info
    const updatedSongs = [...playlist.songs];
    updatedSongs[editingSongIndex] = {
      ...newSongData,
      playerName: editForm.playerName,
      playerNumber: editForm.playerNumber,
      // Reset timing to defaults
      startTime: 0,
      endTime: 20,
      clipDuration: 20,
      aiSelected: false,
      aiReason: null
    };
    
    // Update playlist state
    setPlaylist({
      ...playlist,
      songs: updatedSongs
    });
    
    // Reset edit form timing fields
    setEditForm({
      ...editForm,
      startTime: 0,
      duration: 20,
      useAI: false,
      aiSuggestion: null,
      aiReason: null,
      analyzingAI: false
    });
    
    // Close song search
    setShowChangeSong(false);
  };

  const handlePlaySong = async (song) => {
    if (!playerReady) {
      alert('Spotify player is still loading, please wait a moment and try again.');
      return;
    }
    
    // Check if token is available before playing
    try {
      const token = await getStoredAccessToken();
      if (!token) {
        alert('Your Spotify session has expired. Please click "Reconnect Spotify" at the top of the page to continue playing music.');
        return;
      }
    } catch (error) {
      console.error('Error checking Spotify token:', error);
      alert('Your Spotify session has expired. Please click "Reconnect Spotify" at the top of the page to continue playing music.');
      return;
    }
    
    // Toggle between play and stop
    if (playingUri === song.uri) {
      // Currently playing this song, so stop it
      if (window.stopSpotifyTrack) {
        await window.stopSpotifyTrack();
        setPlayingUri(null);
      }
    } else {
      // Play the song with startTime and duration
      if (window.playSpotifyTrack) {
        const startTime = song.startTime || 0;
        const duration = song.clipDuration || (song.endTime - song.startTime) || 20;
        await window.playSpotifyTrack(song.uri, startTime, duration);
        setPlayingUri(song.uri);
      }
    }
  };

  const handleEditSong = (songIndex) => {
  if (!userCanEdit) {
    alert('You only have view-only access to this playlist.');
    return;
  }
  
  const song = playlist.songs[songIndex];
  const duration = song.clipDuration || (song.endTime - song.startTime) || 20;
  setEditForm({
    playerName: song.playerName || '',
    playerNumber: song.playerNumber !== null && song.playerNumber !== undefined ? song.playerNumber : '',
    startTime: song.startTime || 0,
      duration: duration,
      useAI: song.aiSelected || false,
      aiSuggestion: song.aiSelected ? song.startTime : null,
    aiReason: song.aiReason || null,
    analyzingAI: false
    });
    setEditingSongIndex(songIndex);
  };

  const handleAnalyzeWithAI = async () => {
    const song = playlist.songs[editingSongIndex];
    
    setEditForm(prev => ({ ...prev, analyzingAI: true }));
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const response = await fetch(`${apiUrl}/api/analyze-clip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          songName: song.songName,
          artist: song.artist,
          duration: parseInt(editForm.duration)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to analyze song');
      }

      const aiResult = await response.json();
      
      setEditForm(prev => ({
        ...prev,
        aiSuggestion: aiResult.startTime,
        aiReason: aiResult.reason,
        analyzingAI: false
      }));
      
      console.log('AI analyzed:', aiResult);
    } catch (error) {
      console.error('AI analysis failed:', error);
      alert('AI analysis failed. Please try again or use manual selection.');
      setEditForm(prev => ({ ...prev, analyzingAI: false }));
    }
  };

  const handleSaveEdit = async (e) => {
  e.preventDefault();
  
  // Check permissions before allowing edit
  if (!userCanEdit) {
    alert('You only have view-only access to this playlist.');
    return;
  }
  
  try {
      // If AI mode is on, always re-analyze before saving to get fresh explanation
      let freshAiSuggestion = editForm.aiSuggestion;
      let freshAiReason = editForm.aiReason;

      if (editForm.useAI) {
        const song = playlist.songs[editingSongIndex];
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const aiResponse = await fetch(`${apiUrl}/api/analyze-clip`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            songName: song.songName,
            artist: song.artist,
            duration: parseInt(editForm.duration)
          })
        });
        if (aiResponse.ok) {
          const aiResult = await aiResponse.json();
          freshAiSuggestion = aiResult.startTime;
          freshAiReason = aiResult.reason;
        }
      }

      // Use fresh AI suggestion if in AI mode, otherwise use manual startTime
      const start = editForm.useAI && freshAiSuggestion !== null 
        ? freshAiSuggestion 
        : parseInt(editForm.startTime);
      
      const dur = parseInt(editForm.duration);
      const end = start + dur;
      
      const updatedSongs = [...playlist.songs];
      updatedSongs[editingSongIndex] = {
        ...updatedSongs[editingSongIndex],
        playerName: editForm.playerName,
        playerNumber: parseInt(editForm.playerNumber),
        startTime: start,
        endTime: end,
        clipDuration: dur,
        aiSelected: editForm.useAI,
        aiReason: editForm.useAI ? editForm.aiReason : null
      };
      
      const playlistRef = doc(db, 'playlists', playlist.id);
      await updateDoc(playlistRef, {
        songs: updatedSongs,
        updatedAt: new Date().toISOString()
      });
      
      setEditingSongIndex(null);
      // Real-time listener will auto-update
      console.log('Song updated successfully!');
    } catch (error) {
      console.error('Error updating song:', error);
      alert('Failed to update song: ' + error.message);
    }
  };

  const handleDeleteSong = async (songIndex) => {
  // Check permissions before allowing delete
  if (!userCanEdit) {
    alert('You only have view-only access to this playlist.');
    return;
  }
  
  const confirmed = window.confirm('Are you sure you want to remove this song?');
  
  if (!confirmed) return;
  
  try {
      const updatedSongs = playlist.songs.filter((_, index) => index !== songIndex);
      
      const playlistRef = doc(db, 'playlists', playlist.id);
      await updateDoc(playlistRef, {
        songs: updatedSongs,
        updatedAt: new Date().toISOString()
      });
      
      // Real-time listener will auto-update
      console.log('Song deleted successfully!');
    } catch (error) {
      console.error('Error deleting song:', error);
      alert('Failed to delete song: ' + error.message);
    }
  };

  const handleDragEnd = async (event) => {
  // Check permissions before allowing reorder
  if (!userCanEdit) {
    return;
  }
  
  const { active, over } = event;

  if (!over || active.id === over.id) {
    return;
  }

  const songs = playlist.songs;
  const oldIndex = songs.findIndex((song, idx) => (song.id || `song-${idx}`) === active.id);
  const newIndex = songs.findIndex((song, idx) => (song.id || `song-${idx}`) === over.id);

  if (oldIndex !== -1 && newIndex !== -1) {
    const newSongs = arrayMove(songs, oldIndex, newIndex);
    
    // Update order numbers
    const updatedSongs = newSongs.map((song, index) => ({
      ...song,
      order: index + 1
    }));

    // Update local state immediately for smooth UX
    setPlaylist({
      ...playlist,
      songs: updatedSongs
    });

    // Save to Firebase
    try {
        const playlistRef = doc(db, 'playlists', playlist.id);
        await updateDoc(playlistRef, {
          songs: updatedSongs,
          updatedAt: new Date().toISOString()
        });
        console.log('Song order updated!');
      } catch (error) {
        console.error('Error updating song order:', error);
        // Real-time listener will restore correct state on error
      }
    }
  };

  const handleReconnectSpotify = () => {
    redirectToSpotifyAuth();
  };

  if (!playlist) {
    return (
      <div className="playlist-view-container">
        <div className="playlist-view-header">
          <h2>Playlist not found</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
      </div>
    );
  }

  // If editing a song, show edit form
  if (editingSongIndex !== null) {
    const song = playlist.songs[editingSongIndex];
    return (
      <div className="playlist-view-container">
        <SpotifyPlayer />
        
        {/* Token Expiry Warning Banner in Edit View */}
        {showExpiryWarning && (
          <div className="expiry-warning-banner warning-expired">
            <div className="warning-content">
              <div className="warning-icon">⏰</div>
              <div className="warning-text">
                <strong>Spotify Session Expired</strong>
                <p>Please reconnect to Spotify to test song playback. Your edits will be saved.</p>
              </div>
            </div>
            <div className="warning-actions">
              <button className="warning-btn primary" onClick={handleReconnectSpotify}>
                Reconnect to Spotify
              </button>
            </div>
          </div>
        )}
        
        <div className="playlist-view-header">
          <div>
            <h2>Edit Song</h2>
            <p className="playlist-meta">{song.songName} - {song.artist}</p>
          </div>
          <button className="close-btn" onClick={() => setEditingSongIndex(null)}>×</button>
        </div>

        <div className="playlist-content">
          <form className="edit-song-form" onSubmit={handleSaveEdit}>
            <div className="selected-track-preview">
              {song.albumArt && <img src={song.albumArt} alt={song.album} />}
              <div>
                <div className="song-name">{song.songName}</div>
                <div className="song-artist">{song.artist}</div>
              </div>
              <button 
                type="button"
                className="change-song-btn"
                onClick={() => setShowChangeSong(true)}
                title="Change to a different song"
              >
                ✏️
              </button>
            </div>

            <div className="form-group">
              <label>Player Name *</label>
              <input
                type="text"
                value={editForm.playerName}
                onChange={(e) => setEditForm({...editForm, playerName: e.target.value})}
                required
                placeholder="Enter player name"
              />
            </div>

            <div className="form-group">
              <label>Player Number</label>
              <input
                type="number"
                value={editForm.playerNumber}
                onChange={(e) => setEditForm({...editForm, playerNumber: e.target.value})}
                placeholder="Enter jersey number"
                min="0"
  max="99"
              />
            </div>

            <div className="form-group">
              <label>Clip Selection Method</label>
              <div className="toggle-group">
                <label className={`toggle-option ${!editForm.useAI ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="editSelectionMode"
                    checked={!editForm.useAI}
                    onChange={() => setEditForm({
                      ...editForm, 
                      useAI: false,
                      aiSuggestion: null,
                      aiReason: null
                    })}
                  />
                  <span>Manual Selection</span>
                </label>
                <label className={`toggle-option ${editForm.useAI ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="editSelectionMode"
                    checked={editForm.useAI}
                    onChange={() => setEditForm({...editForm, useAI: true})}
                  />
                  <span>🤖 AI Selection</span>
                </label>
              </div>
            </div>

            {!editForm.useAI && (
              <div className="form-group">
                <label>Start Time (seconds)</label>
                <input
                  type="number"
                  value={editForm.startTime}
                  onChange={(e) => setEditForm({...editForm, startTime: e.target.value})}
                  min="0"
                  placeholder="0"
                />
                <small className="field-hint">Where should the song start playing?</small>
              </div>
            )}

            <div className="form-group">
              <label>Duration (seconds)</label>
              <input
                type="number"
                value={editForm.duration}
                onChange={(e) => setEditForm({
                  ...editForm, 
                  duration: e.target.value,
                  aiSuggestion: null,
                  aiReason: null
                })}
                min="1"
                max="120"
                placeholder="20"
              />
              <small className="field-hint">
                {editForm.useAI 
                  ? 'AI will find the best segment of this length' 
                  : 'How long should it play?'}
              </small>
            </div>

            {editForm.useAI && (
              <>
                <div className="form-group">
                  <button 
  type="button"
  className="analyze-ai-btn"
  onClick={handleAnalyzeWithAI}
  disabled={editForm.analyzingAI}
>
  {editForm.analyzingAI ? (
    <>
      <span className="spinner">⏳</span>
      Analyzing... (This may take 30-60 seconds)
    </>
  ) : '🤖 Analyze with AI'}
</button>
                </div>

                {editForm.aiSuggestion !== null && (
                  <div className="ai-result-box">
                    <div className="ai-result-header">
                      <strong>🤖 AI Analysis Complete</strong>
                    </div>
                    <div className="ai-result-content">
                      <div className="ai-start-time">
                        <strong>Suggested Start Time:</strong> {editForm.aiSuggestion} seconds
                      </div>
                      <div className="ai-reasoning">
                        <strong>Why:</strong> {editForm.aiReason}
                      </div>
                    </div>
                  </div>
                )}

                {!editForm.aiSuggestion && !editForm.analyzingAI && (
                  <div className="ai-info-box">
                    <strong>🤖 AI Mode:</strong> Click "Analyze with AI" and Claude will pick the most energetic {editForm.duration}-second segment for maximum crowd impact! You can adjust the AI recommendation by switching to Manual Selection and entering in your preferred start time.
                  </div>
                )}
              </>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                className="back-btn"
                onClick={() => setEditingSongIndex(null)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={editForm.useAI && editForm.aiSuggestion === null && !editForm.analyzingAI}
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>

        {showChangeSong && (
          <SongSearch
            playlist={playlist}
            onClose={() => setShowChangeSong(false)}
            onSongAdded={handleReplaceSong}
            replacementMode={true}
          />
        )}
      </div>
    );
  }

  return (
    <div className="playlist-view-container">
      <SpotifyPlayer />
      
      {/* Token Expiry Warning Banner */}
      {showExpiryWarning && (
        <div className="expiry-warning-banner warning-expired">
          <div className="warning-content">
            <div className="warning-icon">⏰</div>
            <div className="warning-text">
              <strong>Spotify Session Expired</strong>
              <p>Your Spotify session has timed out. Reconnect to continue playing music. Don't worry - your playlist is safe!</p>
            </div>
          </div>
          <div className="warning-actions">
            <button className="warning-btn primary" onClick={handleReconnectSpotify}>
              Reconnect to Spotify
            </button>
            <button className="warning-btn secondary" onClick={onClose}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
      
      <div className="playlist-view-header">
        <div>
          <h2>{playlist.name}</h2>
          <p className="playlist-meta">
            {playlist.songs?.length || 0} songs
            {!userCanEdit && <span className="view-only-badge"> • 👁️ View Only</span>}
          </p>
        </div>
        <div className="header-actions">
          {userCanEdit && (
            <button className="add-song-btn" onClick={() => setShowSongSearch(true)}>
              + Add Song
            </button>
          )}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="playlist-content">
        {!playerReady && !showExpiryWarning && (
          <div className="player-loading-notice">
            ⏳ Spotify player is loading...
          </div>
        )}
        
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (!playlist.songs || playlist.songs.length === 0) ? (
          <div className="empty-playlist">
            <div className="empty-icon">🎵</div>
            <h3>No songs yet!</h3>
            <p>Add songs to build your walkup music lineup.</p>
            {userCanEdit && (
              <button className="add-song-btn-large" onClick={() => setShowSongSearch(true)}>
                Add Your First Song
              </button>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={playlist.songs.map((song, idx) => song.id || `song-${idx}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="songs-list">
                {playlist.songs.map((song, index) => (
                  <SortableSongItem
                    key={song.id || `song-${index}`}
                    song={song}
                    index={index}
                    playingUri={playingUri}
                    onPlay={handlePlaySong}
                    onEdit={handleEditSong}
                    onDelete={handleDeleteSong}
                    canEdit={userCanEdit}
                    playerReady={playerReady}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {showSongSearch && userCanEdit && (
        <SongSearch
          playlist={playlist}
          onClose={() => setShowSongSearch(false)}
          onSongAdded={handleSongAdded}
        />
      )}
    </div>
  );
}

export default PlaylistView;